/**
 * Journal du processus Bilan : chaque étape enregistre succès/erreur, date, durée,
 * IA utilisée, tokens, input/output pour optimisation et débogage.
 * Le JSON final est téléchargeable en fin de trame.
 */

import { useState, useCallback, useRef } from "react";

export interface StepUsage {
  /** Modèle IA (ex: gemini-2.0-flash, Vertex) */
  model?: string;
  /** Tokens du prompt (entrée) */
  promptTokens?: number;
  /** Tokens de la réponse (sortie) */
  completionTokens?: number;
  /** Total tokens */
  totalTokens?: number;
}

export interface BilanProcessStep {
  stepId: string;
  stepName: string;
  status: "success" | "error" | "running";
  startedAt: string; // ISO
  endedAt?: string; // ISO
  durationMs?: number;
  /** IA utilisée (Gemini, Vertex, etc.) */
  aiUsed?: string;
  usage?: StepUsage;
  /** Payload complet envoyé à l'IA (texte / body de la requête) */
  input?: Record<string, unknown> | string;
  /** Réponse complète reçue de l'IA (texte / body de la réponse) */
  output?: Record<string, unknown> | string;
  /** Message d'erreur si status === "error" */
  error?: string;
  /** Stack trace si disponible */
  errorStack?: string;
  /** Métadonnées additionnelles (userAgent, etc.) */
  extra?: Record<string, unknown>;
}

export interface BilanProcessLogUser {
  id?: string;
  name: string;
  email: string;
  image?: string | null;
}

export interface BilanProcessLog {
  processId: string;
  fileName: string;
  /** Utilisateur ayant lancé le traitement */
  user?: BilanProcessLogUser;
  processStartedAt: string;
  processEndedAt?: string;
  totalDurationMs?: number;
  steps: BilanProcessStep[];
  summary: {
    totalSteps: number;
    successCount: number;
    errorCount: number;
    totalTokensUsed?: number;
  };
  /** Version du schéma pour évolution future */
  schemaVersion: number;
}

const SCHEMA_VERSION = 1;

function generateProcessId(): string {
  return `bilan_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export interface RunningStep {
  stepId: string;
  stepName: string;
  startedAt: string;
  input?: Record<string, unknown> | string;
}

export function useBilanProcessLog() {
  const [processId, setProcessId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [processStartedAt, setProcessStartedAt] = useState<string>("");
  const [steps, setSteps] = useState<BilanProcessStep[]>([]);
  const [currentStep, setCurrentStep] = useState<RunningStep | null>(null);
  const [user, setUserState] = useState<BilanProcessLogUser | null>(null);
  const [savedLogId, setSavedLogId] = useState<string | null>(null);
  const savedLogIdRef = useRef<string | null>(null);

  const setUser = useCallback((u: BilanProcessLogUser | null) => {
    setUserState(u);
  }, []);

  const reset = useCallback((newFileName?: string) => {
    setProcessId(generateProcessId());
    setFileName(newFileName ?? "");
    setProcessStartedAt(new Date().toISOString());
    setSteps([]);
    setCurrentStep(null);
    setSavedLogId(null);
    savedLogIdRef.current = null;
  }, []);

  const startStep = useCallback(
    (stepId: string, stepName: string, input?: Record<string, unknown> | string) => {
      const startedAt = new Date().toISOString();
      setCurrentStep({ stepId, stepName, startedAt, input });
      setSteps((prev) => [
        ...prev,
        {
          stepId,
          stepName,
          status: "running",
          startedAt,
          input,
        },
      ]);
    },
    []
  );

  const endStep = useCallback(
    (
      stepId: string,
      output?: Record<string, unknown> | string,
      usage?: StepUsage,
      error?: { message: string; stack?: string },
      options?: { inputPrompt?: string }
    ) => {
      const endedAt = new Date().toISOString();
      const inputPrompt = options?.inputPrompt;
      setSteps((prev) => {
        const idx = prev.findIndex((s) => s.stepId === stepId && s.status === "running");
        if (idx < 0) return prev;
        const running = prev[idx];
        const started = new Date(running.startedAt).getTime();
        const durationMs = Date.now() - started;
        let input = running.input;
        if (inputPrompt != null && inputPrompt !== "") {
          input =
            typeof input === "object" && input !== null
              ? { ...input, prompt: inputPrompt }
              : { prompt: inputPrompt };
        }
        const step: BilanProcessStep = {
          ...running,
          input,
          status: error ? "error" : "success",
          endedAt,
          durationMs,
          output,
          usage,
          aiUsed: usage?.model,
          error: error?.message,
          errorStack: error?.stack,
        };
        const next = [...prev];
        next[idx] = step;
        return next;
      });
      setCurrentStep((running) => (running?.stepId === stepId ? null : running));
    },
    []
  );

  const getDownloadableJson = useCallback((): BilanProcessLog => {
    const processEndedAt = new Date().toISOString();
    const firstStarted = steps[0]?.startedAt ? new Date(steps[0].startedAt).getTime() : 0;
    const lastEnded = steps.reduce((acc, s) => {
      const end = s.endedAt ? new Date(s.endedAt).getTime() : 0;
      return Math.max(acc, end);
    }, 0);
    const totalDurationMs = lastEnded > 0 ? lastEnded - firstStarted : undefined;
    const successCount = steps.filter((s) => s.status === "success").length;
    const errorCount = steps.filter((s) => s.status === "error").length;
    const totalTokensUsed = steps.reduce((acc, s) => {
      const t = s.usage?.totalTokens ?? (s.usage?.promptTokens ?? 0) + (s.usage?.completionTokens ?? 0);
      return acc + t;
    }, 0);
    return {
      processId: processId ?? generateProcessId(),
      fileName,
      user: user ?? undefined,
      processStartedAt: processStartedAt || new Date().toISOString(),
      processEndedAt,
      totalDurationMs,
      steps,
      summary: {
        totalSteps: steps.length,
        successCount,
        errorCount,
        totalTokensUsed: totalTokensUsed > 0 ? totalTokensUsed : undefined,
      },
      schemaVersion: SCHEMA_VERSION,
    };
  }, [processId, fileName, user, processStartedAt, steps]);

  const downloadLog = useCallback(() => {
    const log = getDownloadableJson();
    const blob = new Blob([JSON.stringify(log, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const baseName = fileName ? fileName.replace(/\.[^/.]+$/, "") : "bilan";
    a.download = `axia_bilan_log_${baseName}_${log.processId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [getDownloadableJson, fileName]);

  const hasSteps = steps.length > 0;

  const saveLog = useCallback(async (): Promise<{ id?: string; error?: string }> => {
    const log = getDownloadableJson();
    const existingId = savedLogIdRef.current ?? savedLogId;
    try {
      if (existingId) {
        const res = await fetch(`/api/bilan-logs/${existingId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(log),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          return { error: (data.error as string) || res.statusText };
        }
        return { id: existingId };
      }
      const res = await fetch("/api/bilan-logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(log),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: (data.error as string) || res.statusText };
      }
      const data = await res.json();
      if (data.id) {
        savedLogIdRef.current = data.id;
        setSavedLogId(data.id);
      }
      return { id: data.id };
    } catch (e) {
      return { error: e instanceof Error ? e.message : "Erreur réseau" };
    }
  }, [getDownloadableJson, savedLogId]);

  return {
    processId,
    fileName,
    steps,
    currentStep,
    hasSteps,
    user,
    setUser,
    reset,
    startStep,
    endStep,
    getDownloadableJson,
    downloadLog,
    saveLog,
  };
}

